import { __awaiter } from "tslib";
import { useEffect, useMemo, useState } from 'react';
import { FfpBonusInfoDocument } from '@websky/graphql';
import { createClient } from '../../../../../graphql-client';
import { useConfig } from '../../../../../context';
import { useTranslation } from 'react-i18next';
import { hasCodeSharedSegments } from '@shared/lib/flight';
export const useGetFfpBonusInfoById = ({ legId, flightIds, segments, needToMakeRequest }) => {
    const client = createClient();
    const { t } = useTranslation('FareGroup');
    const [ffpBonusInfoById, setFfpBonusInfoById] = useState({});
    const [loading, setLoading] = useState(false);
    const { SearchForm: { payByMiles, showFfpBonusInfoInFares } } = useConfig();
    /** Есть ли хоть один КШ сегмент */
    const isCodeShared = useMemo(() => hasCodeSharedSegments(segments), [segments]);
    /**
     * Запрашиваем информацию о милях если:
     * - нужно запрашивать мили (needToMakeRequest === true)
     * - и у а/к есть программа лояльности
     * - и у а/к включен показ начисляемых миль
     * - и рейс не является КШ
     */
    const mustBeRequested = needToMakeRequest && payByMiles && showFfpBonusInfoInFares && !isCodeShared;
    useEffect(() => {
        if (!mustBeRequested) {
            return;
        }
        /**
         * Если для flightId ранее не запрашивались мили, то сохраняем его с loading = true, для отображения загрузки.
         * И сохраняем прошлые состояния, чтобы по новой не показывать лоудер.
         */
        setFfpBonusInfoById(prevState => {
            const nextState = {};
            flightIds.forEach(flightId => {
                if (flightId && !(flightId in prevState)) {
                    nextState[flightId] = {
                        loading: true
                    };
                }
            });
            return Object.assign(Object.assign({}, prevState), nextState);
        });
    }, [flightIds, mustBeRequested]);
    useEffect(() => {
        if (!mustBeRequested) {
            return;
        }
        const getFfpBonusInfo = () => __awaiter(void 0, void 0, void 0, function* () {
            setLoading(true);
            const promises = flightIds.map((flightId) => __awaiter(void 0, void 0, void 0, function* () {
                var _a;
                try {
                    const { data } = yield client.query({
                        query: FfpBonusInfoDocument,
                        variables: { flightId },
                        fetchPolicy: 'cache-first'
                    });
                    const error = t('FfpBonusInfo_Error');
                    const ffpBonusInfo = data === null || data === void 0 ? void 0 : data.FfpBonusInfo;
                    const ffpBonusInfoByLeg = (_a = ffpBonusInfo === null || ffpBonusInfo === void 0 ? void 0 : ffpBonusInfo.bonusMilesByLeg) === null || _a === void 0 ? void 0 : _a.find(info => info.legId === legId);
                    if (!ffpBonusInfo) {
                        return { flightId, ffpBonusInfo: { loading: false, error } };
                    }
                    let bonusMiles;
                    if (ffpBonusInfoByLeg) {
                        /** Если есть legId и для этого плеча есть разбивака, то возвращаем количество миль для конкретного плеча */
                        bonusMiles = ffpBonusInfoByLeg === null || ffpBonusInfoByLeg === void 0 ? void 0 : ffpBonusInfoByLeg.bonusMiles;
                    }
                    else {
                        /** Если нет, то выводим сумму миль за весь перелет */
                        bonusMiles = ffpBonusInfo === null || ffpBonusInfo === void 0 ? void 0 : ffpBonusInfo.bonusMiles;
                    }
                    return {
                        flightId,
                        ffpBonusInfo: { bonusMiles, loading: false }
                    };
                }
                catch (e) {
                    return {
                        flightId,
                        ffpBonusInfo: {
                            loading: false,
                            error: t('FfpBonusInfo_Error')
                        }
                    };
                }
            }));
            const responses = yield Promise.all(promises);
            const result = {};
            responses.forEach(({ flightId, ffpBonusInfo }) => {
                result[flightId] = ffpBonusInfo;
            });
            setFfpBonusInfoById(prev => (Object.assign(Object.assign({}, prev), result)));
            setLoading(false);
        });
        getFfpBonusInfo();
    }, [flightIds, client, legId, mustBeRequested]);
    return {
        ffpBonusInfoById,
        loading
    };
};
